import type { App } from 'vue';
import * as Sentry from '@sentry/vue';
import { SENTRY_DNS, SENTRY_PROPAGATION_TARGETS } from '@Constants';
import { isAbortedRequestError } from '@Providers';
import router from '../libraries/router/index';

// A password-reset token is a credential and must never reach Sentry. It leaks through two default
// behaviours: HttpContext stamps `window.location.href` onto every event's request.url, and
// navigation breadcrumbs record the RESOLVED from/to paths. The scrub runs in beforeBreadcrumb
// (not only beforeSend) so breadcrumbs uploaded inside session replays are covered too. The
// chunk-repair report itself sends the route pattern, never the resolved path
// (see @Composables useChunkPreloadErrorHandling).
const RESET_TOKEN_PATH = /(\/password\/reset\/)[^/?#]+/;

function scrubResetToken(value: string): string {
  return value.replace(RESET_TOKEN_PATH, '$1[token]');
}

export default {
  install(app: App) {
    // NOTE :: Vue won't console AbortedRequestError since they would be just navigation logs
    app.config.errorHandler = (err) => {
      if (isAbortedRequestError(err)) return;
      console.error(err);
    };

    app.config.warnHandler = (msg) => {
      if (msg.includes('AbortedRequestError')) return;
      console.warn(msg);
    };

    // NOTE :: Suppress "Uncaught (in promise) AbortedRequestError" - register at bootstrap so we run before any rejection
    window.addEventListener('unhandledrejection', (event) => {
      if (isAbortedRequestError(event.reason)) {
        event.preventDefault();
      }
    });

    if (!SENTRY_DNS || !SENTRY_PROPAGATION_TARGETS) return;

    Sentry.init({
      app,
      dsn: SENTRY_DNS,
      integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
      tracesSampleRate: 1.0,
      tracePropagationTargets: SENTRY_PROPAGATION_TARGETS,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: [
        // Browser extensions injecting into the page (not our code)
        /runtime\.sendMessage/i,
        // Transient client-network failures (offline / flaky connection) — not actionable.
        // The bare-message entries are anchored on purpose: "Failed to fetch" / "Load failed" alone
        // are dead network calls, but Chrome's "Failed to fetch dynamically imported module: …" is a
        // chunk-load failure we WANT to see — a substring match silently dropped it for months.
        // Sentry tests each pattern against the message and against "{type}: {message}", hence the
        // optional prefix.
        /^(?:AxiosError: )?Network Error$/,
        /^(?:TypeError: )?Failed to fetch$/,
        /^(?:TypeError: )?Load failed$/,
        /NetworkError when attempting to fetch resource/i,
        'timeout of',
        // NOTE :: Chunk-load failures (stale deploy, cached 404, server answering a .js request with
        // index.html) are deliberately NOT ignored. useChunkPreloadErrorHandling only repairs and
        // reports failures that surface through router.onError; a lazy component or dynamic import
        // outside a route change has no other signal than the raw browser error.
        // Browser auto-translate (Google/Safari) mutating the DOM under Vue — framework-internal
        // crashes we don't own. NOTE :: matched by message, not by stack origin: client-side every
        // bundled frame is in_app:true (node_modules is only flagged server-side, after beforeSend),
        // so a frame-based filter cannot run here. These tokens are Vue/PrimeVue internals.
        '__isMounted',
        'emitsOptions',
        "Cannot destructure property 'bum'",
        'nextSibling',
        'parentNode',
        'insertBefore',
        'removeChild',
        'appendChild',
        // PrimeVue TabView / VirtualScroller touching a detached DOM node during teardown/ink-bar update
        'scrollLeft',
        'scrollTop',
        'scrollToIndex',
      ],
      denyUrls: [
        // Errors thrown from browser-extension scripts, not our application
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
        /^safari-(web-)?extension:\/\//,
      ],
      beforeBreadcrumb(breadcrumb) {
        const navigationData = breadcrumb.category === 'navigation' ? breadcrumb.data : undefined;

        if (!navigationData) return breadcrumb;

        if (typeof navigationData.from === 'string') navigationData.from = scrubResetToken(navigationData.from);

        if (typeof navigationData.to === 'string') navigationData.to = scrubResetToken(navigationData.to);

        return breadcrumb;
      },
      // Filter out aborted request errors - these are expected during navigation
      beforeSend(event, hint) {
        const error = hint?.originalException;
        // NOTE :: We won't send AbortedRequestError to Sentry since they are just navigation logs
        if (isAbortedRequestError(error)) return null;

        if (event.request?.url) event.request.url = scrubResetToken(event.request.url);

        return event;
      },
    });
  },
};
